import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionContractDetail } from './hooks/useConstructionContractDetail';
import { useConstructionContracts } from './hooks/useConstructionContracts';
import { Badge, ConfirmDialog } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { useToast } from '@/components/ui/Toast';
import { getStatusConfig, contractStatusMap } from '@/utils/status';
import { ContractFormModal } from './components/ContractFormModal';
import { ContractLegalFormModal } from './components/ContractLegalFormModal';
import { ContractTeamFormModal } from './components/ContractTeamFormModal';

// Tabs
import { OverviewTab } from './tabs/OverviewTab';
import { SidebarInfo } from './tabs/SidebarInfo';
import { ScopeTab } from './tabs/ScopeTab';
import { PlaceholderTab } from './tabs/PlaceholderTab';

export default function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { toast } = useToast();
  const companyId = profile?.company_id || '';

  const { contract: item, isLoading } = useConstructionContractDetail(id);
  const { deleteContract, isDeleting } = useConstructionContracts(companyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showLegalEdit, setShowLegalEdit] = useState(false);
  const [showTeamEdit, setShowTeamEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết hợp đồng...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin hợp đồng</div>;

  const handleDeleteConfirm = async () => {
    if (!item) return;
    try {
      await deleteContract(item.id);
      toast.success('Đã xóa hợp đồng thành công');
      navigate('/construction/contracts');
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'scope', label: 'Phạm vi & BOQ' },
    { value: 'milestones', label: 'Mốc thanh toán' },
    { value: 'parties', label: 'Các bên tham gia' },
    { value: 'variations', label: 'Phát sinh (VO)' },
    { value: 'documents', label: 'Tài liệu' },
  ];

  const statusConfig = getStatusConfig(item.status_code, contractStatusMap);

  return (
    <PageDetailLayout
      title={`${item.contract_name}`}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge variant={statusConfig.variant as any} className="border-transparent text-[10px] font-bold ">
            {statusConfig.label}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{item.contract_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-building-line" />
            {item.project?.name}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Hợp đồng thi công', path: '/construction/contracts' },
        { label: item.contract_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          <AuthButton module="construction" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
          <AuthButton module="construction" action="delete" variant="outline" icon="ri-delete-bin-line" className="text-red-500 hover:bg-red-50" onClick={() => setShowDelete(true)}>
            Xóa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {activeTab === 'overview' && (
            <OverviewTab 
              item={item} 
              onLegalEdit={() => setShowLegalEdit(true)} 
              onTeamEdit={() => setShowTeamEdit(true)} 
            />
          )}
          
          {activeTab === 'scope' && (
            <ScopeTab contractId={item.id} companyId={companyId} />
          )}

          {activeTab === 'milestones' && <PlaceholderTab title="Mốc thanh toán" />}
          {activeTab === 'parties' && <PlaceholderTab title="Các bên tham gia" />}
          {activeTab === 'variations' && <PlaceholderTab title="Phát sinh (VO)" />}
          {activeTab === 'documents' && <PlaceholderTab title="Tài liệu" />}
        </div>

        <div className="space-y-6">
          <SidebarInfo item={item} />
        </div>
      </div>

      <ContractFormModal
        isOpen={showEdit}
        contract={item}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />

      <ContractLegalFormModal
        isOpen={showLegalEdit}
        contract={item}
        companyId={companyId}
        onClose={() => setShowLegalEdit(false)}
      />

      <ContractTeamFormModal
        isOpen={showTeamEdit}
        contract={item}
        companyId={companyId}
        onClose={() => setShowTeamEdit(false)}
      />

      <ConfirmDialog
        isOpen={showDelete}
        title="Xác nhận xóa hợp đồng"
        message={`Bạn có chắc chắn muốn xóa hợp đồng "${item.contract_name}"? Hành động này không thể hoàn tác.`}
        confirmText="Xác nhận xóa"
        isDestructive={true}
        isLoading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onClose={() => setShowDelete(false)}
      />
    </PageDetailLayout>
  );
}
